import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  FileDown,
  FileText,
  FileSpreadsheet,
  Sparkles,
  ArrowUpDown,
} from "lucide-react";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManagePR = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [pressReleases, setPressReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [page, setPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(25);

  const statusTabs = [
    { value: "all", label: "All" },
    { value: "draft", label: "Draft" },
    { value: "pending", label: "Pending" },
    { value: "scheduled", label: "Scheduled" },
    { value: "published", label: "Published" },
    { value: "rejected", label: "Rejected" },
  ];

  useEffect(() => {
    fetchPressReleases();
  }, [status, page, limit]);

  const fetchPressReleases = async (searchQuery = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (status !== "all") params.set("status", status);
      if (searchQuery) params.set("search", searchQuery);

      const response = await fetch(`${API_URL}/api/press-releases?${params}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setPressReleases(data.items);
        setTotalPages(data.pages);
        setTotal(data.total);
      }
    } catch (error) {
      console.error("Failed to fetch press releases:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPressReleases(search);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700 border-slate-200",
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      scheduled: "bg-blue-50 text-blue-700 border-blue-200",
      published: "bg-emerald-50 text-emerald-700 border-emerald-200",
      rejected: "bg-red-50 text-red-700 border-red-200",
      needs_revision: "bg-orange-50 text-orange-700 border-orange-200",
    };
    return styles[status] || styles.draft;
  };

  const handleDownload = (pr, format) => {
    // In production, this would call a backend endpoint
    console.log(`Downloading PR ${pr.pr_id} as ${format}`);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto animate-fade-in">
      {/* AI Performance Banner */}
      <Card className="bg-gradient-to-r from-slate-900 to-slate-800 border-0 text-white overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center border-2 border-slate-900">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center border-2 border-slate-900">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center border-2 border-slate-900">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="font-semibold flex items-center gap-2">
                  <Badge className="bg-emerald-500 text-white border-0">NEW</Badge>
                  <span>AI Performance is now in your report</span>
                </div>
                <p className="text-slate-400 text-sm">
                  Track whether your press release is indexed and referenced across leading AI answer engines.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="bg-transparent border-slate-600 text-white hover:bg-slate-700"
            >
              How it Works?
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-slate-900">
          Manage Press Releases
        </h1>
        <Button
          onClick={() => navigate("/submit-pr")}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="submit-pr-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Submit PR
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Status Tabs */}
            <div className="flex flex-wrap gap-2">
              {statusTabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => {
                    setStatus(tab.value);
                    setPage(1);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    status === tab.value
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`status-tab-${tab.value}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search and Filter */}
            <div className="flex items-center gap-3">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search press release or PR ID"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 w-64"
                  data-testid="search-input"
                />
              </form>
              <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
                <SelectTrigger className="w-20" data-testid="limit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : pressReleases.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <FileText className="h-12 w-12 mb-4 text-slate-300" />
              <p className="text-lg font-medium">No press releases found</p>
              <p className="text-sm">Create your first press release to get started</p>
              <Button
                onClick={() => navigate("/submit-pr")}
                className="mt-4 bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Submit PR
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                    <TableHead className="font-semibold text-slate-600">PR ID</TableHead>
                    <TableHead className="font-semibold text-slate-600">Title</TableHead>
                    <TableHead className="font-semibold text-slate-600">
                      <button className="flex items-center gap-1">
                        Date <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-600">By</TableHead>
                    <TableHead className="font-semibold text-slate-600">Status</TableHead>
                    <TableHead className="font-semibold text-slate-600 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pressReleases.map((pr) => (
                    <TableRow
                      key={pr.pr_id}
                      className="hover:bg-slate-50/50 cursor-pointer"
                      onClick={() => navigate(`/edit-pr/${pr.pr_id}`)}
                      data-testid={`pr-row-${pr.pr_id}`}
                    >
                      <TableCell className="font-medium text-slate-900">{pr.pr_id}</TableCell>
                      <TableCell className="max-w-md truncate text-slate-700">{pr.title}</TableCell>
                      <TableCell className="text-slate-600">
                        {format(new Date(pr.created_at), "dd/M/yyyy")}
                      </TableCell>
                      <TableCell>
                        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-medium">
                          SS
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadge(pr.status)} capitalize border`}>
                          {pr.status.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" data-testid={`pr-action-${pr.pr_id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/edit-pr/${pr.pr_id}`)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View report (.web)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownload(pr, "pdf")}>
                              <FileDown className="h-4 w-4 mr-2" />
                              Download as PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownload(pr, "txt")}>
                              <FileText className="h-4 w-4 mr-2" />
                              Download as TXT
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownload(pr, "excel")}>
                              <FileSpreadsheet className="h-4 w-4 mr-2" />
                              Download as Excel
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {pressReleases.length > 0 && (
            <div className="flex items-center justify-between px-4 py-4 border-t border-slate-200">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span>items per page</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Go to page</span>
                <Input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={page}
                  onChange={(e) => setPage(Math.min(totalPages, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-16 h-8"
                />
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    &lt;
                  </Button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className={page === pageNum ? "bg-blue-600 hover:bg-blue-700" : ""}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  {totalPages > 5 && <span className="text-slate-400">...</span>}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                  >
                    &gt;
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManagePR;
